import { router } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type Product from '@/types/serializers/Product'

export interface SettingsNavItem {
  value: string
  label: string
}

export interface SettingsNavGroup {
  label: string
  items: SettingsNavItem[]
}

// Static (non-brand) groups. Brand sections are injected dynamically between
// Workspace and Inbox.
export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { value: 'general', label: 'General' },
      { value: 'team', label: 'Team' },
      { value: 'products', label: 'Products & Brands' },
      { value: 'channels', label: 'Channels' },
    ],
  },
  {
    label: 'Inbox',
    items: [
      { value: 'inboxes', label: 'Inboxes' },
      { value: 'canned-answers', label: 'Canned answers' },
      { value: 'email', label: 'Email' },
    ],
  },
  {
    label: 'Support',
    items: [
      { value: 'support-hours', label: 'Support hours' },
      { value: 'csat', label: 'Customer satisfaction' },
    ],
  },
  { label: 'Developer', items: [{ value: 'api-keys', label: 'MCP' }] },
  { label: 'Account', items: [{ value: 'billing', label: 'Billing' }] },
]

interface SettingsNavProps {
  brands: Product[]
  current: { tab: string; brand: string | null; channel: string | null }
}

const ITEM_CLASS =
  'w-full cursor-pointer rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground'
const ACTIVE_CLASS = 'bg-muted text-foreground'
const GROUP_LABEL = 'mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60'

export function SettingsNav({ brands, current }: SettingsNavProps) {
  const { withAccount } = useRouteParams()

  const go = (params: { tab: string; brand?: string; channel?: string }) => {
    router.get(routes.settings.show.path(withAccount()), params, { preserveScroll: true })
  }

  const item = (key: string, label: string, active: boolean, onClick: () => void) => (
    <button key={key} onClick={onClick} className={cn(ITEM_CLASS, active && ACTIVE_CLASS)}>
      {label}
    </button>
  )

  const staticGroup = (group: SettingsNavGroup) => (
    <div className="flex flex-col gap-0.5">
      <p className={GROUP_LABEL}>{group.label}</p>
      {group.items.map((i) => item(i.value, i.label, current.tab === i.value, () => go({ tab: i.value })))}
    </div>
  )

  return (
    <nav className="flex w-full flex-col gap-3 md:w-56 md:shrink-0">
      {/* Workspace */}
      {staticGroup(SETTINGS_NAV[0])}

      {/* One dynamic section per brand */}
      {brands.map((brand) => (
        <div key={brand.id} className="flex flex-col gap-0.5 border-t border-border pt-3">
          <p className={cn(GROUP_LABEL, 'flex items-center gap-1.5')}>
            <span
              className="size-2 shrink-0 rounded-full border border-border"
              style={{ backgroundColor: brand.primary_color || '#10b981' }}
              aria-hidden
            />
            {brand.name}
          </p>
          {brand.channels.length === 0
            ? item(
                `add-${brand.id}`,
                '+ Add channel',
                false,
                () => go({ tab: 'channels', brand: brand.slug }),
              )
            : brand.channels.map((c) =>
                c.channel_type === 'help_center'
                  ? item(
                      `channel-${c.id}`,
                      c.name,
                      current.tab === 'help-center' && current.brand === brand.slug,
                      () => go({ tab: 'help-center', brand: brand.slug }),
                    )
                  : item(
                      `channel-${c.id}`,
                      c.name,
                      current.tab === 'widget' && current.channel === c.id,
                      () => go({ tab: 'widget', brand: brand.slug, channel: c.id }),
                    ),
              )}
          {item(
            `email-branding-${brand.id}`,
            'Email branding',
            current.tab === 'email-branding' && current.brand === brand.slug,
            () => go({ tab: 'email-branding', brand: brand.slug }),
          )}
        </div>
      ))}

      {/* Inbox / Support / Developer / Account */}
      {SETTINGS_NAV.slice(1).map((group) => (
        <div key={group.label} className="flex flex-col gap-0.5 border-t border-border pt-3">
          <p className={GROUP_LABEL}>{group.label}</p>
          {group.items.map((i) => item(i.value, i.label, current.tab === i.value, () => go({ tab: i.value })))}
        </div>
      ))}
    </nav>
  )
}
